<script setup>
// To be used to preserve network bandwidth for user-contributed images
// by only requesting the image at the size it will be displayed at.

import { watchThrottled } from '@vueuse/core'

const props = defineProps([
  'src',
  'alt',
  'width',
  'height',
])

const el = ref(null)
const { width: detectedWidth, height: detectedHeight } = useElementSize(el)
let widthMax = -1
let heightMax = -1

const parameterizedUrl = ref(undefined)

const computedWidth = computed(() => +props.width || detectedWidth.value || 0)
const computedHeight = computed(() => +props.height || detectedHeight.value || 0)

function updateParameterizedUrl() {
  if (!computedWidth.value && !computedHeight.value)
    return

  if (computedWidth.value <= widthMax || computedHeight.value <= heightMax)
    return

  widthMax = Math.max(widthMax, computedWidth.value)
  heightMax = Math.max(heightMax, computedHeight.value)

  const [urlWithoutQueryString] = props.src.split('?')
  parameterizedUrl.value = `${urlWithoutQueryString}?${computedWidth.value > 0 ? `w=${Math.ceil(computedWidth.value)}&` : ''}${computedHeight.value > 0 ? `h=${Math.ceil(computedHeight.value)}` : ''}`
}

watchThrottled(
  () => [computedWidth, computedHeight],
  () => updateParameterizedUrl(),
  {
    immediate: true,
    deep: true,
    throttle: 1000,
    maxWait: 5000,
  },
)
</script>

<template>
  <component
    :is="parameterizedUrl ? 'img' : 'div'"
    ref="el"
    :src="parameterizedUrl"
    v-bind="{
      ...(width && { width }),
      ...(height && { height }),
    }"
    :alt="alt"
  />
</template>
